import { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Activity,
  ArrowRight,
  BarChart3,
  Bot,
  CheckCircle2,
  Cpu,
  CreditCard,
  FileSpreadsheet,
  Layers,
  ScanLine,
  ShieldCheck,
  ShoppingBag,
  Smartphone,
  Sparkles,
  Users,
  Warehouse,
  Zap,
} from 'lucide-react'

export function LandingPage() {
  const [activeTab, setActiveTab] = useState<
    'pos' | 'inventory' | 'ai' | 'accounting' | 'marketplace'
  >('pos')

  const features = [
    {
      id: 'pos',
      label: 'Point of Sale (POS)',
      icon: ScanLine,
      title: 'High-Speed Retail Checkout & Till Shifts',
      description:
        'Engineered for peak retail rush hours with sub-5ms catalog lookups, multi-tender payment processing, cash drawer reconciliation, and instant thermal receipt generation.',
      image: '/images/pos-checkout.jpg',
      link: '/login',
      highlights: [
        'Sub-5ms in-memory catalog scan caching',
        'Split payments: Cash, Card, Prepaid Store Wallet',
        'Automated cashier shift opening, mid-day floats, and closing reconciliation',
        'Customer purchase history and instant loyalty credit lookup',
      ],
    },
    {
      id: 'inventory',
      label: 'Warehouse & Inventory',
      icon: Warehouse,
      title: 'Multi-Location Stock Control & Costing',
      description:
        'End-to-end inventory management featuring real-time stock allocation, Weighted Average Costing (WAC) updates on purchase orders, and multi-tier approval workflows for adjustments.',
      image: '/images/warehouse-inventory.jpg',
      link: '/login',
      highlights: [
        'Weighted Average Cost (WAC) recalculated on every purchase receipt',
        'Stock adjustment requests with two-tier manager approval queue',
        'Multi-warehouse transfers and location-based batch tracking',
        'Automated low-stock threshold triggers and reorder warnings',
      ],
    },
    {
      id: 'ai',
      label: 'AI & Analytics Intelligence',
      icon: Bot,
      title: 'Machine Learning Cross-Sell & Demand Forecasting',
      description:
        'Built-in ML engines continuously analyze trading patterns to surface high-probability basket add-ons at checkout, detect pricing anomalies, and forecast product replenishment.',
      image: '/images/ai-analytics.jpg',
      link: '/login',
      highlights: [
        'Apriori Market Basket Mining for real-time cashier recommendations',
        'Statistical price anomaly detection across customer tiers and regions',
        'Executive revenue, GMV, and inventory velocity rollup dashboards',
        'Continuous ML retraining backed by high-throughput Redis cache',
      ],
    },
    {
      id: 'accounting',
      label: 'General Ledger & Tax',
      icon: FileSpreadsheet,
      title: 'Automated Dual-Regime Accounting',
      description:
        'A unified Chart of Accounts that automatically generates double-entry journal postings from every POS sale, credit invoice, and inventory adjustment with zero reconciliation drift.',
      image: '/images/hero-dashboard.jpg',
      link: '/login',
      highlights: [
        'Zero-touch posting from POS tickets and sales invoices to the General Ledger',
        'Full compliance with India GST (CGST/SGST/IGST + HSN) and US Sales Tax',
        'Real-time Trial Balance, Balance Sheet, and P&L statements',
        'Period locking with tamper-evident audit logging for financial governance',
      ],
    },
    {
      id: 'marketplace',
      label: 'Consumer Marketplace',
      icon: ShoppingBag,
      title: 'A Public Storefront on the Same Database',
      description:
        'The shop is not a second system with a nightly sync. It reads the same catalog, reserves the same stock and writes the same sales orders as the counter and the field rep, so a marketplace sale is visible in the ERP the moment it is paid for.',
      image: '/images/marketplace-storefront.jpg',
      //: The only tab whose call to action is not the ERP login -- shoppers have no staff
      //: account, and sending them to a sign-in page would be sending them away.
      link: '/store',
      highlights: [
        'One catalog: a "listed on marketplace" flag, with a bulk action to publish in batches',
        'Consumer signup and checkout with Stripe, in Indian Rupees or US Dollars',
        'Shipping quoted at checkout, with free delivery above a configurable order value',
        'Every order lands in Sales as a marketplace-channel order, taxed by the same engine',
      ],
    },
  ] as const

  const currentFeature = features.find((f) => f.id === activeTab) || features[0]

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 selection:bg-emerald-500 selection:text-white font-sans">
      {/* Navigation Topbar */}
      <header className="sticky top-0 z-50 border-b border-slate-800/80 bg-slate-950/80 backdrop-blur-md">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-tr from-emerald-600 to-cyan-500 shadow-lg shadow-emerald-500/20">
              <Layers className="h-5 w-5 text-white" />
            </div>
            <div>
              <span className="text-lg font-bold tracking-tight text-white">
                FMCG <span className="text-emerald-400">ERP Suite</span>
              </span>
              <span className="hidden sm:inline-block ml-2 rounded-full bg-emerald-500/10 px-2 py-0.5 text-xs font-medium text-emerald-400 border border-emerald-500/20">
                AI-Powered
              </span>
            </div>
          </div>

          <nav className="hidden md:flex items-center gap-6 text-sm font-medium text-slate-400">
            <a href="#features" className="hover:text-emerald-400 transition-colors">
              Modules
            </a>
            <a href="#architecture" className="hover:text-emerald-400 transition-colors">
              Architecture
            </a>
            <a href="#roles" className="hover:text-emerald-400 transition-colors">
              RBAC Matrix
            </a>
            <Link
              to="/store"
              className="hover:text-emerald-400 transition-colors flex items-center gap-1.5"
            >
              <ShoppingBag className="h-3.5 w-3.5 text-emerald-400" />
              Marketplace
            </Link>
            <Link
              to="/status"
              className="hover:text-emerald-400 transition-colors flex items-center gap-1.5"
            >
              <Activity className="h-3.5 w-3.5 text-emerald-400" />
              Live Status
            </Link>
          </nav>

          <div className="flex items-center gap-3">
            <a
              href="https://api.fmcgdemo.elvirainfotech.org/docs"
              target="_blank"
              rel="noreferrer"
              className="hidden sm:inline-flex items-center gap-1.5 rounded-lg border border-slate-800 bg-slate-900/60 px-3.5 py-1.5 text-xs font-medium text-slate-300 hover:border-slate-700 hover:bg-slate-800 transition-all"
            >
              <Cpu className="h-3.5 w-3.5 text-cyan-400" />
              API Docs
            </a>
            <Link
              to="/login"
              className="inline-flex items-center gap-2 rounded-lg bg-emerald-500 px-4 py-2 text-sm font-semibold text-slate-950 shadow-md shadow-emerald-500/25 hover:bg-emerald-400 transition-all active:scale-95"
            >
              <span>Launch Demo</span>
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden pt-12 pb-20 sm:pt-20 sm:pb-28">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_80%_at_50%_-20%,rgba(16,185,129,0.15),rgba(255,255,255,0))]" />

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto space-y-6">
            <div className="inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-950/40 px-4 py-1.5 text-xs font-medium text-emerald-300 backdrop-blur-sm">
              <Sparkles className="h-3.5 w-3.5 text-emerald-400" />
              Enterprise FMCG Distribution & Retail Suite
            </div>

            <h1 className="text-4xl font-extrabold tracking-tight sm:text-6xl text-white">
              Next-Gen ERP for <br className="hidden sm:inline" />
              <span className="bg-gradient-to-r from-emerald-400 via-cyan-300 to-blue-500 bg-clip-text text-transparent">
                Fast-Moving Consumer Goods
              </span>
            </h1>

            <p className="text-base sm:text-lg text-slate-300 leading-relaxed">
              Unify high-speed POS checkout, multi-warehouse inventory valuation, automated
              dual-regime tax compliance, and real-time machine learning cross-sell recommendations
              on a single high-performance platform.
            </p>

            <div className="flex flex-wrap items-center justify-center gap-4 pt-4">
              <Link
                to="/login"
                className="inline-flex items-center gap-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 px-6 py-3.5 text-base font-semibold text-slate-950 shadow-xl shadow-emerald-500/20 hover:from-emerald-400 hover:to-teal-400 transition-all hover:scale-105 active:scale-95"
              >
                <span>Sign In to ERP Dashboard</span>
                <ArrowRight className="h-5 w-5" />
              </Link>
              {/* The shopper's way in. Everything else on this page needs a staff account;
                  the storefront needs none, so it cannot sit behind "Sign In to ERP". */}
              <Link
                to="/store"
                className="inline-flex items-center gap-2 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-5 py-3.5 text-base font-medium text-emerald-200 hover:bg-emerald-500/20 hover:border-emerald-500/50 transition-all"
              >
                <ShoppingBag className="h-4 w-4 text-emerald-400" />
                <span>Shop the Marketplace</span>
              </Link>
              <Link
                to="/status"
                className="inline-flex items-center gap-2 rounded-xl border border-slate-800 bg-slate-900/80 px-5 py-3.5 text-base font-medium text-slate-200 hover:bg-slate-800 hover:border-slate-700 transition-all"
              >
                <Activity className="h-4 w-4 text-emerald-400" />
                <span>System Health Probe</span>
              </Link>
            </div>

          </div>

          {/* Hero Mockup Frame */}
          <div className="relative mt-12 sm:mt-16 rounded-2xl border border-slate-800 bg-slate-900/50 p-2 sm:p-4 shadow-2xl shadow-emerald-500/10 backdrop-blur-xl">
            <div className="overflow-hidden rounded-xl border border-slate-800/80 bg-slate-950">
              <img
                src="/images/hero-dashboard.jpg"
                alt="FMCG ERP Executive Dashboard"
                className="w-full object-cover object-top max-h-[560px] rounded-lg shadow-inner"
              />
            </div>

            {/* Floating Live Metric Pills */}
            <div className="absolute -bottom-5 left-8 hidden sm:flex items-center gap-2.5 rounded-xl border border-slate-700/80 bg-slate-900/90 px-4 py-2.5 shadow-xl backdrop-blur-md text-xs font-medium text-slate-200">
              <Zap className="h-4 w-4 text-amber-400" />
              <span>Sub-5ms POS Scan Engine</span>
            </div>
            <div className="absolute -bottom-5 right-8 hidden sm:flex items-center gap-2.5 rounded-xl border border-slate-700/80 bg-slate-900/90 px-4 py-2.5 shadow-xl backdrop-blur-md text-xs font-medium text-slate-200">
              <ShieldCheck className="h-4 w-4 text-emerald-400" />
              <span>Zero-Drift General Ledger</span>
            </div>
          </div>
        </div>
      </section>

      {/* Metrics Banner */}
      <section className="border-y border-slate-800/80 bg-slate-900/40 py-10">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 gap-6 lg:grid-cols-4 text-center">
            <div className="space-y-1">
              <div className="text-3xl font-extrabold text-white sm:text-4xl">&lt; 5 ms</div>
              <div className="text-xs uppercase tracking-wider text-slate-400 font-medium">
                Catalog Scan Latency
              </div>
            </div>
            <div className="space-y-1">
              <div className="text-3xl font-extrabold text-emerald-400 sm:text-4xl">100%</div>
              <div className="text-xs uppercase tracking-wider text-slate-400 font-medium">
                Dual Tax Compliance (GST & US)
              </div>
            </div>
            <div className="space-y-1">
              <div className="text-3xl font-extrabold text-cyan-400 sm:text-4xl">Real-Time</div>
              <div className="text-xs uppercase tracking-wider text-slate-400 font-medium">
                Weighted Average Costing (WAC)
              </div>
            </div>
            <div className="space-y-1">
              <div className="text-3xl font-extrabold text-purple-400 sm:text-4xl">88 / 10</div>
              <div className="text-xs uppercase tracking-wider text-slate-400 font-medium">
                Permissions & System Roles
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Features Deep Dive Section */}
      <section id="features" className="py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto space-y-4 mb-14">
            <h2 className="text-xs font-semibold uppercase tracking-widest text-emerald-400">
              Core Architecture
            </h2>
            <p className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
              Engineered Specifically for FMCG Velocity
            </p>
            <p className="text-sm sm:text-base text-slate-400">
              Every module is designed to handle high transaction volumes with sub-second
              responsiveness, audit trails, and strict data consistency.
            </p>
          </div>

          {/* Feature Tabs */}
          <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-10">
            {features.map((tab) => {
              const Icon = tab.icon
              const isActive = activeTab === tab.id
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium transition-all ${
                    isActive
                      ? 'bg-emerald-500 text-slate-950 shadow-lg shadow-emerald-500/20'
                      : 'bg-slate-900 text-slate-400 hover:bg-slate-800 hover:text-slate-200 border border-slate-800'
                  }`}
                >
                  <Icon className={`h-4 w-4 ${isActive ? 'text-slate-950' : 'text-slate-400'}`} />
                  <span>{tab.label}</span>
                </button>
              )
            })}
          </div>

          {/* Active Tab Card */}
          <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6 sm:p-10 backdrop-blur-xl">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
              <div className="lg:col-span-6 space-y-6">
                <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400">
                  <currentFeature.icon className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white sm:text-3xl">
                    {currentFeature.title}
                  </h3>
                  <p className="mt-3 text-slate-300 leading-relaxed text-sm sm:text-base">
                    {currentFeature.description}
                  </p>
                </div>

                <div className="space-y-3 pt-2">
                  {currentFeature.highlights.map((h, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-emerald-400 shrink-0 mt-0.5" />
                      <span className="text-sm text-slate-300">{h}</span>
                    </div>
                  ))}
                </div>

                <div className="pt-4">
                  <Link
                    to={currentFeature.link}
                    className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-400 hover:text-emerald-300 transition-colors"
                  >
                    <span>
                      {currentFeature.id === 'marketplace'
                        ? 'Open the marketplace — no account needed'
                        : 'Test this module in live environment'}
                    </span>
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>

              <div className="lg:col-span-6">
                <div className="relative rounded-xl border border-slate-700/80 bg-slate-950 p-2 shadow-2xl">
                  <img
                    src={currentFeature.image}
                    alt={currentFeature.title}
                    className="w-full h-80 sm:h-96 object-cover rounded-lg"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-transparent to-transparent rounded-lg" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Role-Based Access Matrix & Field Rep Section */}
      <section id="roles" className="border-t border-slate-800 bg-slate-900/30 py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto space-y-4 mb-14">
            <h2 className="text-xs font-semibold uppercase tracking-widest text-cyan-400">
              Workforce Orchestration
            </h2>
            <p className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
              10 Specialized Role Personas & Mobile Field Operations
            </p>
            <p className="text-sm sm:text-base text-slate-400">
              Each team member operates within strict RBAC boundaries and scoped record views, from
              field sales vans to corporate controllers.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="rounded-xl border border-slate-800 bg-slate-900/80 p-6 space-y-3 hover:border-slate-700 transition-all">
              <div className="h-10 w-10 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-blue-400">
                <Smartphone className="h-5 w-5" />
              </div>
              <h4 className="text-lg font-semibold text-white">Field Sales Representative</h4>
              <p className="text-xs text-slate-400 leading-relaxed">
                Mobile-optimized route execution, customer visit check-in with GPS validation,
                instant order taking, and real-time quota achievement tracking.
              </p>
            </div>

            <div className="rounded-xl border border-slate-800 bg-slate-900/80 p-6 space-y-3 hover:border-slate-700 transition-all">
              <div className="h-10 w-10 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400">
                <ScanLine className="h-5 w-5" />
              </div>
              <h4 className="text-lg font-semibold text-white">Store & POS Cashier</h4>
              <p className="text-xs text-slate-400 leading-relaxed">
                Counter checkout with barcode scanner integration, cash drawer shift balancing,
                prepaid store cards, and AI-suggested cross-sell prompts.
              </p>
            </div>

            <div className="rounded-xl border border-slate-800 bg-slate-900/80 p-6 space-y-3 hover:border-slate-700 transition-all">
              <div className="h-10 w-10 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-400">
                <Warehouse className="h-5 w-5" />
              </div>
              <h4 className="text-lg font-semibold text-white">Warehouse & Inventory Manager</h4>
              <p className="text-xs text-slate-400 leading-relaxed">
                Purchase receiving, inventory lot allocation, stock adjustment approvals, scrap
                tracking, and reorder dispatching across regional hubs.
              </p>
            </div>

            <div className="rounded-xl border border-slate-800 bg-slate-900/80 p-6 space-y-3 hover:border-slate-700 transition-all">
              <div className="h-10 w-10 rounded-lg bg-purple-500/10 border border-purple-500/20 flex items-center justify-center text-purple-400">
                <CreditCard className="h-5 w-5" />
              </div>
              <h4 className="text-lg font-semibold text-white">Finance & Billing Controller</h4>
              <p className="text-xs text-slate-400 leading-relaxed">
                Automated invoice dispatching, customer credit limits, payment reconciliations, GST
                / US tax filings, and General Ledger auditing.
              </p>
            </div>

            <div className="rounded-xl border border-slate-800 bg-slate-900/80 p-6 space-y-3 hover:border-slate-700 transition-all">
              <div className="h-10 w-10 rounded-lg bg-rose-500/10 border border-rose-500/20 flex items-center justify-center text-rose-400">
                <Users className="h-5 w-5" />
              </div>
              <h4 className="text-lg font-semibold text-white">HR & Performance Reviewer</h4>
              <p className="text-xs text-slate-400 leading-relaxed">
                Objective staff performance reviews calculated from sales metrics, manager scores,
                attendance records, and commission payouts.
              </p>
            </div>

            <div className="rounded-xl border border-slate-800 bg-slate-900/80 p-6 space-y-3 hover:border-slate-700 transition-all">
              <div className="h-10 w-10 rounded-lg bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center text-cyan-400">
                <BarChart3 className="h-5 w-5" />
              </div>
              <h4 className="text-lg font-semibold text-white">Executive & Sales Director</h4>
              <p className="text-xs text-slate-400 leading-relaxed">
                Real-time GMV velocity, regional demand trends, price-elasticity analytics, customer
                churn prediction, and company-wide KPI monitoring.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Tech Stack & Architecture */}
      <section id="architecture" className="border-t border-slate-800 py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto space-y-4 mb-14">
            <h2 className="text-xs font-semibold uppercase tracking-widest text-emerald-400">
              Modern Enterprise Stack
            </h2>
            <p className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
              Scalable, Resilient & Modular by Design
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-5 space-y-2">
              <div className="text-lg font-bold text-white">FastAPI + AsyncPG</div>
              <p className="text-xs text-slate-400">
                Python 3.12 asynchronous core API with OpenAPI documentation
              </p>
            </div>
            <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-5 space-y-2">
              <div className="text-lg font-bold text-white">PostgreSQL 16</div>
              <p className="text-xs text-slate-400">
                Transactional outbox, pg_trgm fuzzy search, and double-entry ledger
              </p>
            </div>
            <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-5 space-y-2">
              <div className="text-lg font-bold text-white">Redis 7 + Celery</div>
              <p className="text-xs text-slate-400">
                Low-latency catalog caching, ML reco storage, and async rollup workers
              </p>
            </div>
            <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-5 space-y-2">
              <div className="text-lg font-bold text-white">React 18 + Vite</div>
              <p className="text-xs text-slate-400">
                Tailwind CSS, TanStack Query, Recharts, and mobile-ready Capacitor shell
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action Card */}
      <section className="py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-3xl border border-emerald-500/30 bg-gradient-to-r from-emerald-950/60 via-slate-900 to-cyan-950/60 p-8 sm:p-14 text-center space-y-6 shadow-2xl">
            <h3 className="text-3xl font-extrabold text-white sm:text-4xl">
              Ready to Experience the AI-Powered FMCG ERP?
            </h3>
            <p className="max-w-2xl mx-auto text-sm sm:text-base text-slate-300">
              Access the live environment with pre-seeded trading history, product catalogs, POS
              terminals, and full accounting ledgers.
            </p>
            <div className="flex flex-wrap justify-center gap-4 pt-2">
              <Link
                to="/login"
                className="inline-flex items-center gap-2 rounded-xl bg-emerald-500 px-7 py-3.5 text-base font-semibold text-slate-950 shadow-xl shadow-emerald-500/30 hover:bg-emerald-400 transition-all active:scale-95"
              >
                <span>Access Demo Environment</span>
                <ArrowRight className="h-5 w-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-800/80 bg-slate-950 py-10">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-slate-500">
          <div>
            &copy; {new Date().getFullYear()} AI-Powered FMCG ERP Suite. Deployed on Elvira Infotech
            Cloud Infrastructure.
          </div>
          <div className="flex items-center gap-6">
            <Link to="/store" className="hover:text-emerald-400 transition-colors">
              Marketplace
            </Link>
            <Link to="/status" className="hover:text-emerald-400 transition-colors">
              System Status
            </Link>
            <a
              href="https://api.fmcgdemo.elvirainfotech.org/docs"
              target="_blank"
              rel="noreferrer"
              className="hover:text-emerald-400 transition-colors"
            >
              API Reference
            </a>
            <Link to="/login" className="hover:text-emerald-400 transition-colors">
              Sign In
            </Link>
          </div>
        </div>
      </footer>
    </div>
  )
}
